<script>
// @ts-check
import DateField from '@/components/fields/DateField.vue'
import InfoIcon from '@/components/icons/InfoIcon.vue'
import { useVuelidate } from '@/core/composables/useVuelidate'
import {
  FORMAT_VALUE_DATE,
  dateToString,
  reformatDateString
} from '@/helpers/dates'
import { formatToFinnishCurrency } from '@/helpers/formatters'
import { getDocumentDateRange } from '@/helpers/voucher-dates'
import { present as presentInfo } from '@/presenters/infoText'
import { required } from '@vuelidate/validators'
import { mapGetters } from 'vuex'

export default {
  name: 'MarkExpenseAsFullyPaid',
  components: {
    DateField,
    InfoIcon
  },
  props: {
    entry: {
      /** @type {import('vue').PropType<EntryResource>} */
      type: Object,
      required: true
    }
  },
  emits: ['success', 'error', 'cancel'],
  setup: () => useVuelidate(),
  validations: () => ({
    paid_date: {
      required
    }
  }),
  data: () => ({
    hasPaidExpense: true,
    paid_date: dateToString(new Date(), FORMAT_VALUE_DATE),
    disabledFrom: null,
    disabledUntil: null
  }),
  computed: {
    ...mapGetters('companyData', ['isInDemoMode']),
    outstandingSum() {
      return formatToFinnishCurrency(this.entry.outstanding_sum)
    },
    description() {
      return this.entry.description
    },
    formattedDate() {
      return reformatDateString(this.entry.date)
    },
    infoText() {
      return presentInfo({
        disabledFrom: this.disabledFrom,
        disabledUntil: this.disabledUntil
      }).paymentInfo
    },
    canMarkAsPaid() {
      return this.entry.can_mark_as_paid
    }
  },
  async mounted() {
    if (this.isInDemoMode) {
      return
    }

    const { min, max } = await getDocumentDateRange()
    this.disabledUntil = min
    this.disabledFrom = max
  },
  methods: {
    async submitPaymentEvent() {
      if (!this.canMarkAsPaid) {
        this.$emit('cancel')
        return
      }

      try {
        await this.$store.dispatch(`expenses/markAsPaid`, {
          id: this.entry.id,
          type: 'expense',
          payload: {
            amount: this.entry.outstanding_sum,
            date: this.paid_date
          }
        })
        this.$emit('success')
      } catch (error) {
        this.setServerValidationMessages(error)
        this.$emit('error', error)
      }
    }
  }
}
</script>

<template>
  <div>
    <table class="entry-details mb-4 w-full table-fixed">
      <tbody>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.date') }}</th>
          <td>{{ formattedDate }}</td>
        </tr>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.amount') }}</th>
          <td>{{ outstandingSum }}</td>
        </tr>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.description') }}</th>
          <td>{{ description }}</td>
        </tr>
      </tbody>
    </table>
    <InfoAlert class="mt-4">
      <p>{{ $t('mark_as_paid.mark_as_fully_paid_info') }}</p>
    </InfoAlert>
    <div class="mt-4">
      <DateField
        v-model="v$.paid_date.$model"
        class="required"
        :label="$t('entries.actions.mark-as-paid-date')"
        :error-messages="errors.paid_date"
        :min="disabledUntil"
        :max="disabledFrom"
      >
        <template #append-inner>
          <InfoIcon v-tooltip="infoText" />
        </template>
      </DateField>
      <div class="mt-4 flex items-start fill-current">
        <VIcon
          icon="$information-outline"
          class="mr-1 text-primary"
          size="small"
        />
        <p>
          <span>
            {{ $t('expense_payment.payment_date') }}
          </span>
        </p>
      </div>
    </div>
  </div>
</template>
